import CertificateMultipleService from 'app_services/admin/certificateMultiple/CertificateMultipleService'
import { ICourseSchedulingEnrollmentListItem } from 'app_types/admin/courseScheduling/courseSchedulingEnrollmentTypes'
import { ICourseSchedulingListItem } from 'app_types/admin/courseScheduling/courseSchedulingTypes'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { CertificateSettingType, ICertificatesStudent, ICertificateStudentProcessed } from 'app_types/admin/certificateSettings/certificateSettings.types';
import TextComponent from 'app_components/commons/TextComponent/TextComponent'
import DataTableComponent from 'app_components/commons/dataTable';
import LoadingWrapper from 'app_components/commons/LoadingWrapper/LoadingWrapper'
import AlertComponent from 'app_components/commons/alert'
import TooltipComponent from 'app_components/commons/TooltipComponent/TooltipComponent'
import { BiDownload } from 'react-icons/bi'
import { ScreenNotificationsUtil } from 'app_utils/screenNotification'
import './ModalEnrollmentCertificates.scss'
import ButtonComponent from 'app_components/commons/Button/ButtonComponent'
import SavingIndicator from 'app_components/commons/SavingIndicator/SavingIndicator'
import { BounceLoader } from 'react-spinners'
import { IoIosRefreshCircle } from 'react-icons/io'
import { useColors } from 'app_hooks/useColors'
import { useTranslation } from 'react-i18next'
import { I18nUtil } from 'app_utils/i18n'
import CertificateService from 'app_services/user/certificateService'
import { MdError } from 'react-icons/md'

interface IModalEnrollmentCertificatesProps {
  onClose: (saved?: boolean) => void
  open: boolean
  enrollment: ICourseSchedulingEnrollmentListItem
  courseScheduling: ICourseSchedulingListItem
}

const ModalEnrollmentCertificates: FC<IModalEnrollmentCertificatesProps> = ({
  onClose,
  open,
  enrollment,
  courseScheduling,
}) => {
  const [colors] = useColors()
  const certificateMultipleService = useMemo(() => new CertificateMultipleService(), [])
  const [certification, setCertification] = useState<ICertificatesStudent | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const screenNotifications = new ScreenNotificationsUtil()
  const i18nUtil = new I18nUtil()
  const { t } = useTranslation()
  const certificateService: CertificateService = new CertificateService()
  const [isReissued, setIsReissued] = useState<boolean>(false)

  const getCertificates = useCallback(async () => {
    if (!courseScheduling?._id || !enrollment?.user?._id) return
    setIsLoading(true)
    try {
      const response = await certificateMultipleService.getCertificatesByStudent({
        course_scheduling: courseScheduling._id,
        studentId: enrollment.user._id,
        only_certificates: true
      })
      if (response?.code === 200) {
        setCertification(response.student)
      } else {
        setCertification(null)
      }
    } catch (e) {
      console.error('Certificate data by student ERROR: ', e)
    }
    setIsLoading(false)
  }, [certificateMultipleService, courseScheduling?._id, enrollment?.user?._id])

  useEffect(() => {
    getCertificates()
  }, [getCertificates])

  const handleCloseModal = () => {
    if(isReissued) {
      window.location.reload()
    } else {
      onClose()
    }
  }

  const getDataTableData = () => {
    if (!certification?.certifications?.length) return []
    return certification?.certifications?.filter((certificate) => certificate?.certificate?.isGenerated)?.map((certificate) => ({
      ...certificate,
      certificateTypeName: certificate?.certificateType === CertificateSettingType.ATTENDANCE ? 'Asistencia' : 'Asistencia y aprobación'
    }))
  }

  const handleClickDownloadCertificate = (certificate: ICertificateStudentProcessed) => {
    if (certificate?.certificate?.certificateUrl) {
      window.open(certificate?.certificate?.certificateUrl, '_blank', 'noreferrer')
    }
  }

  const certificationReIssue = (row: ICertificateStudentProcessed) => {
    let title = `${t('screens.course_scheduling.certifications.re_issue.title')}`
    let icon = <IoIosRefreshCircle size='20px' color={colors.primary} />
    let component = (
      <button
        className='btn btn-link btn-sm'
        data-tip={title}
        onClick={() => handlereGenerateCertificate(row)}
      >
        {icon}
      </button>
    )
    return component
  }

  const handlereGenerateCertificate = async (row: ICertificateStudentProcessed) => {
    let confirm = screenNotifications.fire({
      message: `${i18nUtil.getTranslate('screens.course_scheduling.certifications.re_issue.confirmation.message')}`,
    },
    {
      type: 'warning',
      title: `${i18nUtil.getTranslate('screens.course_scheduling.certifications.re_issue.confirmation.title')}`,
      confirmButtonText: `${i18nUtil.getTranslate('screens.course_scheduling.certifications.re_issue.confirmation.confirmButtonText')}`,
      cancelButtonText: `${i18nUtil.getTranslate('components.screen_notifications.buttons.cancelButtonText')}`,
      showCancelButton: true,
    })

    if (confirm) {
      confirm.then(async (result) => {
        if (result.value) {
          screenNotifications.fireProcessing()

          const response = await certificateService.reGenerateCertification({
            isMultiple: true,
            certificateQueueId: row.certificate.certificateId,
          })

          if (response) {
            screenNotifications.fireToast(
              {
                message: `${i18nUtil.getTranslate('screens.course_scheduling.certifications.re_issue.confirmation.success')}`,
              },
              {
                position: 'top',
              },
            )
            if (certification?.certifications) {
              setIsReissued(true)
              const newCertification = certification?.certifications.filter((c) => c.certificate.certificateId !== row.certificate.certificateId)
              setCertification({
                ...certification,
                certifications: newCertification
              })
            }
          }
        }
      })
    }
  }

  const getCertificateStatusComponent = (row: ICertificateStudentProcessed) => {
    if (row.certificate.isGenerated) {
      if (row.certificate.certificateStatus === 'Complete') {
        return (
          <>
            <LoadingWrapper
              loading={row?.certificate?.isGenerated && !row?.certificate?.certificateUrl}
              size='sm'
            >
              <TooltipComponent text='Descargar certificado' >
                <BiDownload
                  onClick={() => handleClickDownloadCertificate(row)}
                  color='var(--primary)'
                  size={20}
                  cursor="pointer"
                />
              </TooltipComponent>
            </LoadingWrapper>
            {certificationReIssue(row)}
          </>
        )
      } else if (row.certificate.certificateStatus === 'Error') {
        return (
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <TooltipComponent
              text={`Error al generar el certificado ${row.certificate.errorMessage}`}
            >
              <MdError size='20px' color={'red'} />
            </TooltipComponent>
          </div>
        )
      } else {
        return (
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <TooltipComponent
              text={`El certificado se encuentra en estado ${row.certificate.certificateStatus}`}
            >
              <SavingIndicator
                state={'saving'}
                savingIcon={(
                  <BounceLoader
                    color={`var(--orange)`}
                    size={10}
                  />
                )}
              />
            </TooltipComponent>
          </div>
        )
      }
    }
    return;
  }

  return (
    <Modal
      show={open}
      onHide={handleCloseModal}
      size='lg'
    >
      <Modal.Header>
        <Modal.Title>
          Certificados
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="modal-enrollment-certificates--layout">
          <TextComponent color="black">
            {enrollment?.user?.profile?.first_name} {enrollment?.user?.profile?.last_name}
          </TextComponent>
          <LoadingWrapper loading={isLoading}>
            {certification?.certifications?.length ? (
              <DataTableComponent
                data={getDataTableData()}
                keys={['certificateName', 'certificateTypeName']}
                headerKeys={['Nombre del certificado', 'Tipo de certificado']}
                tooltipKeys={['certificateName']}
                rowButtons={(row: ICertificateStudentProcessed) => (
                  <div className='modal-enrollment-certificates--table-actions'>
                    {getCertificateStatusComponent(row)}
                  </div>
                )}
              />
            ) : (
              <AlertComponent description='No hay certificados disponibles' />
            )}
          </LoadingWrapper>
          <div className="modal-enrollment-certificates--actions">
            <ButtonComponent onClick={handleCloseModal}>
              Cerrar
            </ButtonComponent>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default ModalEnrollmentCertificates
